<template>
  <el-popover
    v-if="chipVisible"
    placement="bottom"
    width="340"
    trigger="click"
    popper-class="offline-sync-popover"
  >
    <div class="sync-panel">
      <div class="sync-panel-header">
        <i :class="offline ? 'fa-solid fa-link-slash' : 'fa-solid fa-cloud-arrow-up'"></i>
        <strong>{{ offline ? "You're offline" : "Sync status" }}</strong>
      </div>

      <p class="sync-explain" v-if="offline">
        Clock-ins and clock-outs still work — they're saved on this device and
        upload automatically as soon as you're back online.
      </p>
      <p class="sync-explain" v-else-if="pendingItems.length">
        Saved work is uploading automatically. Keep this tab open until the
        list below is empty.
      </p>

      <div v-if="pendingItems.length" class="sync-section">
        <div class="sync-section-title">Waiting to upload</div>
        <div v-for="item in pendingItems" :key="item.id" class="sync-item">
          <i class="fa-regular fa-clock mr-1"></i>
          <span>{{ itemLabel(item) }}</span>
          <span class="sync-item-time">{{ formatTime(item.timestamp) }}</span>
        </div>
        <el-button
          v-if="!offline"
          size="mini"
          type="primary"
          class="mt-1"
          @click="syncNow"
        >
          Sync now
        </el-button>
      </div>

      <div v-if="failedItems.length" class="sync-section sync-failed">
        <div class="sync-section-title">
          <i class="fa-solid fa-triangle-exclamation mr-1"></i>
          Needs attention
        </div>
        <div v-for="item in failedItems" :key="item.id" class="sync-item">
          <div>
            <span>{{ itemLabel(item) }}</span>
            <span class="sync-item-time">{{ formatTime(item.timestamp) }}</span>
          </div>
          <div class="sync-item-error">{{ item.lastError }}</div>
          <el-button size="mini" type="text" @click="discardItem(item)">
            Discard
          </el-button>
        </div>
        <el-button size="mini" type="warning" class="mt-1" @click="retryAll">
          Retry all
        </el-button>
      </div>

      <div class="sync-section" v-if="cacheSummary.length">
        <div class="sync-section-title">Saved for offline use</div>
        <div v-for="entry in cacheSummary" :key="entry.category" class="sync-item">
          <i class="fa-solid fa-database mr-1"></i>
          <span>{{ entry.category }}</span>
          <span class="sync-item-time">updated {{ formatTime(entry.newest) }}</span>
        </div>
        <p class="sync-explain">
          This data lets the clock-in and clock-out forms open without
          internet. It refreshes automatically while you're online.
        </p>
      </div>
      <div class="sync-section" v-else-if="offline">
        <p class="sync-explain">
          No offline data has been saved yet on this device — the clock forms
          may not open until you're back online.
        </p>
      </div>
    </div>

    <span slot="reference" class="offline-sync-chip" :class="chipClass">
      <i :class="chipIcon"></i>
      {{ chipText }}
    </span>
  </el-popover>
</template>

<script>
import { Popover } from "element-ui";
import {
  getPending,
  getFailed,
  onChange,
  retryFailed,
  removeItem,
  flushNow,
} from "@/utils/offlineQueue";
import { getCacheSummary } from "@/utils/clockDataCache";

export default {
  name: "OfflineSyncStatus",
  // el-popover is not in the app's global Element UI registrations
  // (globalComponents.js) — register it locally.
  components: { ElPopover: Popover },
  data() {
    return {
      offline: !navigator.onLine,
      pendingItems: getPending(),
      failedItems: getFailed(),
      cacheSummary: [],
    };
  },
  computed: {
    chipVisible() {
      return this.offline || this.pendingItems.length > 0 || this.failedItems.length > 0;
    },
    chipClass() {
      if (this.failedItems.length) return "chip-failed";
      if (this.offline) return "chip-offline";
      return "chip-pending";
    },
    chipIcon() {
      if (this.failedItems.length) return "fa-solid fa-triangle-exclamation";
      if (this.offline) return "fa-solid fa-link-slash";
      return "fa-solid fa-cloud-arrow-up";
    },
    chipText() {
      if (this.failedItems.length) {
        return `${this.failedItems.length} need attention`;
      }
      if (this.offline) {
        const n = this.pendingItems.length;
        return n ? `Offline — ${n} saved` : "Offline";
      }
      return `Syncing ${this.pendingItems.length}…`;
    },
  },
  mounted() {
    this._unsubscribe = onChange(() => {
      this.pendingItems = getPending();
      this.failedItems = getFailed();
    });
    this._onOnline = () => {
      this.offline = false;
    };
    this._onOffline = () => {
      this.offline = true;
      this.refreshCacheSummary();
    };
    window.addEventListener("online", this._onOnline);
    window.addEventListener("offline", this._onOffline);
    this.refreshCacheSummary();
    this._cacheTimer = setInterval(() => this.refreshCacheSummary(), 60000);
  },
  beforeDestroy() {
    if (this._unsubscribe) this._unsubscribe();
    window.removeEventListener("online", this._onOnline);
    window.removeEventListener("offline", this._onOffline);
    if (this._cacheTimer) clearInterval(this._cacheTimer);
  },
  methods: {
    refreshCacheSummary() {
      getCacheSummary().then((summary) => {
        this.cacheSummary = summary;
      });
    },
    itemLabel(item) {
      if (item.label) return item.label;
      if (item.type === "clock-in") return "Clock-in";
      if (item.type === "clock-out") return "Clock-out";
      if (item.type === "complete-shift-notes") return "Shift notes";
      return item.type;
    },
    formatTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
      return sameDay ? time : `${d.toLocaleDateString()} ${time}`;
    },
    async syncNow() {
      const result = await flushNow();
      if (result.succeeded > 0) {
        this.$message.success(
          `${result.succeeded} offline item(s) uploaded successfully.`,
        );
      } else if (result.aborted) {
        this.$message.warning(
          "Still can't reach the server — will retry automatically.",
        );
      }
    },
    retryAll() {
      retryFailed();
      this.syncNow();
    },
    discardItem(item) {
      this.$confirm(
        `Discard "${this.itemLabel(item)}" recorded ${this.formatTime(item.timestamp)}? ` +
          "This permanently deletes the saved work — it will never reach the server.",
        "Discard offline item",
        {
          confirmButtonText: "Discard",
          cancelButtonText: "Keep",
          type: "warning",
        },
      )
        .then(() => {
          removeItem(item.id);
          this.$message.info("Offline item discarded.");
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.offline-sync-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  color: #fff;
}
.chip-offline {
  background-color: #f56c6c;
}
.chip-pending {
  background-color: #e6a23c;
}
.chip-failed {
  background-color: #c0392b;
}

.sync-panel {
  font-size: 13px;
}
.sync-panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 6px;
}
.sync-explain {
  color: #666;
  margin: 4px 0 8px;
  line-height: 1.4;
}
.sync-section {
  border-top: 1px solid #eee;
  padding-top: 8px;
  margin-top: 8px;
}
.sync-section-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.sync-item {
  padding: 3px 0;
  line-height: 1.35;
}
.sync-item-time {
  color: #999;
  margin-left: 6px;
  font-size: 12px;
}
.sync-item-error {
  color: #c0392b;
  font-size: 12px;
  margin: 2px 0;
}
.sync-failed .sync-section-title {
  color: #c0392b;
}
</style>
